<template lang="pug">
reporting-layout
  v-layout.header-title(align-center justify-start row fill-height)
    action-title-bar.title-bar(is-inline hide-subtitle :title='bulkActionReportSelected')
      span.ml-2.title-complement {{ subtitle }}
  v-layout.mt-4(fluid align-center)
    v-menu(offset-y)
      v-toolbar-title.liondesk(slot='activator')
        span.font-weight-regular {{ bulkActionReportSelected }}
        v-icon(color='primary', right) arrow_drop_down
      v-list
        v-list-tile(
          v-for='option in options'
          :key='option.value'
          @click='selectMenuOption(option)'
        ) 
          v-list-tile-title
            | {{ option.text }}
    span.ml-3.total-description {{ contactsBulkActionsTotal }} Total Bulk Actions
  v-layout.mt-4(fluid)
    v-card.flex
      v-container.pa-0(fluid, :class='{ "in-progress": contactsBulkActionsInProgress }')
        v-data-table.lonewolf-table(
          hide-actions
          :headers='reportingHeaders'
          :items='contactsBulkActions'
          :loading='contactsBulkActionsInProgress'
        )
          template(slot="items" slot-scope="data")
            tr
              td.py-0 {{ data.item.description }}
              td(v-if="archived") {{ data.item.contacts_total }}
              td(v-else)
                a(@click="openContactsModal(data.item)") {{ data.item.contacts_total }}
              td.text-xs-center
                bulk-action-status-field(:status="data.item.status")
              td 
                span(:class="getStatusStyle(data.item.status)") {{ data.item.percentage }}%
              td {{ data.item.created_at | formatDate }}
              td.text-xs-right.actions
                v-tooltip(top v-if="isCancelCampaignBulkAction(data.item.description)")
                  v-btn.mr-3(
                    icon
                    slot='activator'
                    :disabled="isBulkActionPending(data.item.status)"
                    @click="handleStartCampaign(data.item.master_id)"
                  )
                    v-icon(medium) add_circle
                  span(v-if="!isBulkActionPending(data.item.status)") Start a new campaign for all successful contacts
                  span(v-else) The action is pending to complete
          template(slot="no-data")
            v-layout.mb-4.mt-3.py-4.pr-4(column align-center)
              v-flex.mb-2
                v-icon(size=40) info
              v-flex
                span.title
                  | No Bulk Actions created yet
              v-flex
                span.subheading Your Bulk Actions will be shown here.
          template(v-if="hasBulkAcionsLeftToFetch" slot="footer")
            scroll-observer(@intersecting="infiniteScroll")
  bulk-actions-contacts-modal(
    ref="contactsModal"
    :bulk-action-id="activeBulkAction.master_id"
    :bulk-action-status="activeBulkAction.status"
    :date-created="activeBulkAction.created_at"
    @start-campaign="openStartCampaignModal"
  )
  start-campaign-modal(
    ref="startCampaigns"
    :start-campaigns="startCampaigns"
    :selected-ids="selectedBulkActionContactIds"
    @contact-updated="refreshTable"
  )
</template>
  
<script>
import { createNamespacedHelpers, mapActions, mapGetters } from 'vuex';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import ScrollObserver from '@/Core/components/ScrollObserver';
import { formatDate } from '@/Core/filters/dates';
import { 
  bulkActionsReportOptions, 
  headers, 
  DONE_WITH_ERRORS_STATUS,
  PENDING_STATUS,
} from '../config/bulk-actions-report-options';
import ReportingLayout from '../Layout';
import BulkActionStatusField from './BulkActionStatusField';
import BulkActionsContactsModal from './BulkActionsContactsModal';

const { 
  mapState: mapBulkActionsReportingState, 
  mapActions: mapBulkActionsReportingActions,
  mapGetters: mapBulkActionsReportingGetters,
  } = createNamespacedHelpers('bulkActionsReport');

export default {
  name: 'BulkActionsReportV2',
  filters: {
    formatDate,
  },
  components: {
    ReportingLayout,
    ActionTitleBar,
    BulkActionStatusField,
    ScrollObserver,
    BulkActionsContactsModal,
    StartCampaignModal: () => import(/* webpackChunkName: 'StartCampaignModal' */ '@/Contacts/components/actions/StartCampaignModal'),
  },
  data() {
    return {
      reportingHeaders: headers,
      archived: false,
      bulkActionReportSelected: bulkActionsReportOptions[0].text,
      options: bulkActionsReportOptions,
      pagination: { page: 1, descending: true, sortBy: 'created_at' },
      activeBulkAction: {},
    };
  },
  computed: {
    ...mapBulkActionsReportingState([
      'contactsBulkActions',
      'contactsBulkActionsTotal',
      'contactsBulkActionsInProgress',
      'selectedBulkAction',
    ]),
    ...mapBulkActionsReportingGetters(['hasBulkAcionsLeftToFetch']),
    subtitle() {
      return !this.archived ? '(past 30 days)' : '(older than 30 days)';
    },
    selectedBulkActionContactIds() {
      return this.selectedBulkAction.ids || [];
    },
  },
  mounted() {
    this.getBulkActionsReport();
  },
  methods: {
    ...mapBulkActionsReportingActions([
      'getContactsBulkActions',
      'getBulkActionsContacts',
    ]),
    ...mapActions('contactsTable/bulkActions', ['processBulkActionFollowup']),
    ...mapActions('contactsSearchCampaign', ['findCampaigns']),
    selectMenuOption(option) {
      this.bulkActionReportSelected = option.text;
      this.archived = option.isArchivedOption;
      this.pagination.page = 1;
      this.getBulkActionsReport();
    },
    getBulkActionsReport() {
      this.getContactsBulkActions({
        archived: this.archived,
        pagination: this.pagination,
      });
    },
    refreshTable() {
      this.pagination.page = 1;
      this.getBulkActionsReport();
    },
    getStatusStyle(status) {
      switch (status) {
        case DONE_WITH_ERRORS_STATUS:
          return 'percentage-text-error';
        case PENDING_STATUS:
          return 'percentage-text-pending';
        default:
          return 'percentage-text';
      }
    },
    infiniteScroll() {
      this.pagination.page++;
      if (this.hasBulkAcionsLeftToFetch) {
        this.getBulkActionsReport();
      }
    },
    openContactsModal(bulkAction) {
      this.activeBulkAction = bulkAction;
      this.$refs.contactsModal.modal_open();
    },
    isCancelCampaignBulkAction(description) {
      return description.startsWith('Canceled campaign');
    },
    isBulkActionPending(status) {
      return status === PENDING_STATUS;
    },
    async startCampaigns(campaign) {
      const body = {
        payload: campaign,
        masterId: this.selectedBulkAction.master_id,
      };

      await this.processBulkActionFollowup(body);
    },
    async openStartCampaignModal() {
      await this.findCampaigns();
      this.$refs.startCampaigns.modal_open();
    },
    async handleStartCampaign(masterId) {
      const params = {
        bulkActionId: masterId,
        status: 'success',
        limit: 10,
        page: 1,
      };

      await this.getBulkActionsContacts(params)
      this.openStartCampaignModal();
    },
  },
};
</script>
  
<style lang="scss" scoped>
.title-complement {
  font-size: 20px !important;
  font-weight: 500;
}

.total-description {
  color: var(--v-text-lighten3);
}

.percentage-text {
  color: var(--v-text-lighten3);
}

.percentage-text-pending {
  color: var(--v-primary-lighten2);
}

.percentage-text-error {
  color: red;
}
</style>
